import { MoreHorizontal, RotateCcw } from "lucide-react";
import { Button } from "@/core/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/core/components/ui/dropdown-menu";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { dayjs } from "@/core/utils/date";
import { cn } from "@/core/utils/utils";
import { VIEWER_OVERLAY_CLASSNAME } from "@/payroll/ai-script/ai-script-spreadsheet-viewer/utils";
import type { ICellComment, ICellCommentThread } from "./types";

// Marks menus portaled out of the comment card, so the card can tell a click
// inside one of them from a click away from it.
export const CELL_COMMENT_OVERLAY_ATTRIBUTE = "data-cell-comment-overlay";

export const isInsideCellCommentOverlay = (target: EventTarget | null) =>
  target instanceof Element &&
  !!target.closest(`[${CELL_COMMENT_OVERLAY_ATTRIBUTE}]`);

const commentTime = (at: string) => {
  const time = dayjs(at);
  return time.isSame(dayjs(), "year")
    ? time.format("MMM D, h:mm A")
    : time.format("MMM D, YYYY, h:mm A");
};

// Destructive and rare, so it waits behind a hover-revealed "more" control
// instead of sitting under every comment. Still reachable by keyboard: the
// control shows on focus too.
const CommentActions = ({ onDelete }: { onDelete: () => void }) => (
  <DropdownMenu modal={false}>
    <DropdownMenuTrigger asChild>
      <button
        type="button"
        aria-label="More options"
        className="flex size-6 shrink-0 items-center justify-center rounded-md text-tertiary-text opacity-0 transition-opacity hover:bg-bg-secondary hover:text-primary-text focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring group-hover:opacity-100 data-[state=open]:opacity-100"
      >
        <MoreHorizontal className="size-4" aria-hidden />
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent
      align="end"
      className={VIEWER_OVERLAY_CLASSNAME}
      {...{ [CELL_COMMENT_OVERLAY_ATTRIBUTE]: "" }}
    >
      <DropdownMenuItem
        className="text-negative-text focus:text-negative-text"
        onSelect={onDelete}
      >
        Delete comment
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

const CommentItem = ({
  comment,
  onDelete,
}: {
  comment: ICellComment;
  onDelete: () => void;
}) => (
  <Stack gap="none" align="stretch" fullWidth className="group min-w-0">
    <Stack direction="row" align="center" gap="sm" fullWidth>
      <Typography variant="subtitleW3" className="min-w-0 flex-1 truncate">
        {comment.author.full_name}
      </Typography>
      <Typography
        variant="bodyW3"
        textColor="tertiary"
        className="shrink-0 text-[12px]"
      >
        {commentTime(comment.created_at)}
      </Typography>
      {comment.mine && !comment.deleted ? (
        <CommentActions onDelete={onDelete} />
      ) : (
        <span aria-hidden className="size-6 shrink-0" />
      )}
    </Stack>
    {comment.deleted ? (
      <Typography variant="bodyW3" textColor="tertiary" className="italic">
        Comment deleted
      </Typography>
    ) : (
      <Typography
        variant="bodyW2"
        className="whitespace-pre-wrap break-words pr-8 leading-relaxed"
      >
        {comment.body}
      </Typography>
    )}
  </Stack>
);

/**
 * One thread's comments, oldest first. A resolved thread reads dimmed and says
 * who closed it, with the way to reopen it; resolving an open one lives with
 * the surface's own header, not here.
 */
export const CellCommentThreadView = ({
  thread,
  isUpdating,
  onDeleteComment,
  onReopen,
}: {
  thread: ICellCommentThread;
  isUpdating?: boolean;
  onDeleteComment: (commentId: string) => void;
  onReopen: () => void;
}) => (
  <Stack gap="sm" align="stretch" fullWidth>
    <Stack
      gap="sm"
      align="stretch"
      fullWidth
      className={cn({ "opacity-60": !!thread.resolved_at })}
    >
      {thread.comments.map((comment) => (
        <CommentItem
          key={comment.id}
          comment={comment}
          onDelete={() => onDeleteComment(comment.id)}
        />
      ))}
    </Stack>
    {thread.resolved_at && (
      <Stack direction="row" align="center" gap="sm">
        <Typography
          variant="bodyW3"
          textColor="tertiary"
          className="text-[12px]"
        >
          Resolved by {thread.resolved_by?.full_name}
        </Typography>
        <Button
          variant="text"
          size="xs"
          className="gap-1 px-1 [&_svg]:size-3.5"
          disabled={isUpdating}
          onClick={onReopen}
        >
          <RotateCcw aria-hidden />
          Reopen
        </Button>
      </Stack>
    )}
  </Stack>
);
